/**
 * Assumption Nodes - User-Adjustable Inputs
 *
 * These values can be adjusted by the user via the inspector panel.
 * Each has presets based on the existing SCENARIO_OPTIONS.
 */

import { assumption, AssumptionNode, Citation } from '../../lib/computation-graph';

// Helper to convert existing scenario options to presets format
function makePresets(
  scenarios: Record<string, { value: number; label: string; description: string; citation: string; link: string }>
): Record<string, { value: number; label: string; description: string; citation: Citation }> {
  const presets: Record<string, { value: number; label: string; description: string; citation: Citation }> = {};
  for (const [key, scenario] of Object.entries(scenarios)) {
    presets[key] = {
      value: scenario.value,
      label: scenario.label,
      description: scenario.description,
      citation: {
        source: scenario.citation,
        url: scenario.link,
      },
    };
  }
  return presets;
}

export const assumptions: Record<string, AssumptionNode> = {
  // Property Value
  propertyValue: assumption('propertyValue', 2161000, {
    label: 'Property Value',
    description: 'Current market value of the property',
    unit: 'currency',
    range: { min: 1500000, max: 3500000, step: 10000 },
    presets: makePresets({
      'conservative': { value: 2000000, label: 'Conservative ($2.0M)', description: 'Below current estimates, assuming market softening', citation: 'Zillow Market Analysis', link: 'https://www.zillow.com/homedetails/2040-Beryl-Ln-Newport-Beach-CA-92660/25212911_zpid/' },
      'current_estimate': { value: 2161000, label: 'Zillow Estimate ($2.16M)', description: 'Current Zillow Zestimate for 2040 Beryl Lane (Dec 2025)', citation: 'Zillow Zestimate', link: 'https://www.zillow.com/homedetails/2040-Beryl-Ln-Newport-Beach-CA-92660/25212911_zpid/' },
      'redfin_estimate': { value: 2281000, label: 'Redfin Estimate ($2.28M)', description: 'Redfin automated valuation model estimate (Dec 2025)', citation: 'Redfin AVM', link: 'https://www.redfin.com/CA/Newport-Beach/2040-Beryl-Ln-92660/home/3558891' },
      'optimistic': { value: 2500000, label: 'Optimistic ($2.5M)', description: 'Strong Newport Beach market with premium location', citation: 'Orange County Market Data', link: 'https://www.zillow.com/homedetails/2040-Beryl-Ln-Newport-Beach-CA-92660/25212911_zpid/' },
    }),
  }),

  // Monthly Rent
  monthlyRent: assumption('monthlyRent', 6980, {
    label: 'Monthly Rent',
    description: 'Expected monthly rental income',
    unit: 'currency',
    range: { min: 4000, max: 12000, step: 100 },
    presets: makePresets({
      'conservative': { value: 5500, label: 'Conservative ($5.5K)', description: 'Below market rent, quick tenant placement', citation: 'Zillow Rental Estimate', link: 'https://www.zillow.com/homedetails/2040-Beryl-Ln-Newport-Beach-CA-92660/25212911_zpid/' },
      'zillow_estimate': { value: 6980, label: 'Zillow Estimate ($7K)', description: 'Zillow Rent Zestimate for 2040 Beryl Lane (Dec 2025)', citation: 'Zillow Rent Zestimate', link: 'https://www.zillow.com/homedetails/2040-Beryl-Ln-Newport-Beach-CA-92660/25212911_zpid/' },
      'market_average': { value: 7500, label: 'Market Average ($7.5K)', description: 'Average for comparable 3BR homes in area', citation: 'Newport Beach Rental Market', link: 'https://www.apartmentlist.com/rent-report/ca/newport-beach' },
      'premium': { value: 8500, label: 'Premium ($8.5K)', description: 'High-end finishes and premium location pricing', citation: 'Newport Beach Rental Market', link: 'https://www.apartmentlist.com/rent-report/ca/newport-beach' },
    }),
  }),

  // Property Appreciation (real, inflation-adjusted)
  propertyAppreciation: assumption('propertyAppreciation', 3.5, {
    label: 'Property Appreciation Rate',
    description: 'Annual real appreciation rate (inflation-adjusted)',
    unit: 'percent',
    range: { min: 0, max: 8, step: 0.1 },
    presets: makePresets({
      'conservative': { value: 0.5, label: 'Conservative (0.5%)', description: 'Real appreciation below historical average', citation: 'Federal Reserve Economic Data (FRED)', link: 'https://fred.stlouisfed.org/series/CSUSHPINSA' },
      'historical_national': { value: 2.3, label: 'National Long-term (2.3%)', description: 'Real Case-Shiller national 30-year average', citation: 'Case-Shiller National Index', link: 'https://www.longtermtrends.com/home-price-vs-inflation/' },
      'historical_la': { value: 3.5, label: 'LA Regional (3.5%)', description: 'Real LA Case-Shiller 25-year average (LA outperforms national)', citation: 'Case-Shiller LA Index (LXXRSA)', link: 'https://fred.stlouisfed.org/series/LXXRSA' },
      'recent_strong': { value: 4.5, label: 'Recent Performance (4.5%)', description: 'Real last 10 years including post-COVID surge', citation: 'Real Estate Decoded', link: 'https://realestatedecoded.com/case-shiller/' },
      'optimistic': { value: 5.5, label: 'Optimistic (5.5%)', description: 'Real continued strong coastal CA performance', citation: 'Newport Beach Housing Market', link: 'https://www.redfin.com/city/13193/CA/Newport-Beach/housing-market' },
    }),
  }),

  // Rent Growth (real, inflation-adjusted)
  rentGrowth: assumption('rentGrowth', 1.0, {
    label: 'Rent Growth Rate',
    description: 'Annual real rent growth rate (inflation-adjusted)',
    unit: 'percent',
    range: { min: -2, max: 5, step: 0.1 },
    presets: makePresets({
      'inflation': { value: 0.0, label: 'Inflation Rate (0%)', description: 'Real rents stay flat with inflation', citation: 'Bureau of Labor Statistics CPI', link: 'https://www.bls.gov/news.release/cpi.nr0.htm' },
      'modest': { value: 1.0, label: 'Modest Growth (1%)', description: 'Real rents grow slightly above inflation', citation: 'Apartment List Rent Report', link: 'https://www.apartmentlist.com/rent-report/ca/newport-beach' },
      'strong': { value: 2.0, label: 'Strong Growth (2%)', description: 'Real rent growth in high-demand coastal market', citation: 'Newport Beach Rental Market', link: 'https://www.apartmentlist.com/rent-report/ca/newport-beach' },
      'very_strong': { value: 3.0, label: 'Very Strong (3%)', description: 'Real rent growth with supply constraints', citation: 'Newport Beach Rental Market', link: 'https://www.apartmentlist.com/rent-report/ca/newport-beach' },
    }),
  }),

  // Stock Returns (real, inflation-adjusted)
  stockReturns: assumption('stockReturns', 7.5, {
    label: 'Stock Returns',
    description: 'Expected annual real stock market return (S&P 500)',
    unit: 'percent',
    range: { min: 2, max: 15, step: 0.1 },
    presets: makePresets({
      'conservative': { value: 5.5, label: 'Conservative (5.5%)', description: 'Below historical real average, accounts for high valuations', citation: 'Vanguard Market Outlook', link: 'https://corporate.vanguard.com/content/corporatesite/us/en/corp/articles/vanguard-economic-market-outlook-2025-global-summary.html' },
      'historical_longterm': { value: 7.5, label: 'Historical Long-term (7.5%)', description: 'Real S&P 500 average since 1926 (inflation-adjusted)', citation: 'Investopedia S&P 500 Returns', link: 'https://www.investopedia.com/ask/answers/042415/what-average-annual-return-sp-500.asp' },
      'historical_20yr': { value: 8.0, label: 'Historical 20-year (8%)', description: 'Real 20-year average including 2008 crisis', citation: 'MacroTrends S&P 500 Data', link: 'https://www.macrotrends.net/2526/sp-500-historical-annual-returns' },
      'recent_strong': { value: 10.5, label: 'Recent Strong (10.5%)', description: 'Real last 10 years performance', citation: 'MacroTrends S&P 500 Data', link: 'https://www.macrotrends.net/2526/sp-500-historical-annual-returns' },
      'optimistic': { value: 12.5, label: 'Optimistic (12.5%)', description: 'Real bull market continuation scenario', citation: 'Bull Market Performance Data', link: 'https://www.investopedia.com/terms/b/bullmarket.asp' },
    }),
  }),

  // Bond Returns (real, inflation-adjusted)
  bondReturns: assumption('bondReturns', 1.5, {
    label: 'Bond Returns',
    description: 'Expected annual real bond return',
    unit: 'percent',
    range: { min: -2, max: 5, step: 0.1 },
    presets: makePresets({
      'low': { value: -0.5, label: 'Low (-0.5%)', description: 'Real negative return in low rate environment', citation: 'Federal Reserve Economic Data', link: 'https://fred.stlouisfed.org/series/DGS10' },
      'current': { value: 1.5, label: 'Current (1.5%)', description: 'Real return with recent Treasury rates', citation: 'U.S. Treasury Data', link: 'https://home.treasury.gov/resource-center/data-chart-center/interest-rates/TextView?type=daily_treasury_yield_curve&field_tdr_date_value=2025' },
      'historical': { value: 2.5, label: 'Historical (2.5%)', description: 'Real long-term bond market average', citation: 'NYU Stern Bond Returns', link: 'https://pages.stern.nyu.edu/~adamodar/New_Home_Page/datafile/histretSP.html' },
      'rising_rates': { value: 3.5, label: 'Rising Rates (3.5%)', description: 'Real return in higher rate environment', citation: 'Bond Market Outlook', link: 'https://www.fidelity.com/learning-center/trading-investing/bond-market-outlook' },
    }),
  }),

  // Property Tax Rate
  propertyTaxRate: assumption('propertyTaxRate', 1.1, {
    label: 'Property Tax Rate',
    description: 'Annual property tax as percentage of assessed value',
    unit: 'percent',
    range: { min: 0.5, max: 1.5, step: 0.05 },
    presets: makePresets({
      'low': { value: 0.75, label: 'Low (0.75%)', description: 'Favorable assessment or tax-advantaged area', citation: 'Orange County Tax Assessor', link: 'https://www.ocgov.com/residents/property-taxes-assessments' },
      'typical': { value: 1.0, label: 'Reassessed (1.0%)', description: 'Post-Prop 19 reassessment to market value (inherited rental)', citation: 'CA Prop 19 (2020)', link: 'https://www.boe.ca.gov/proptaxes/prop19.htm' },
      'high': { value: 1.1, label: 'High (1.1%)', description: 'Reassessed value plus local assessments and bonds', citation: 'California Property Tax Analysis', link: 'https://www.tax-rates.org/california/property-tax' },
      'very_high': { value: 1.25, label: 'Very High (1.25%)', description: 'Reassessed with all local special assessments', citation: 'SmartAsset CA Property Tax', link: 'https://smartasset.com/taxes/california-property-tax-calculator' },
    }),
  }),

  // Vacancy Rate
  vacancyRate: assumption('vacancyRate', 5.0, {
    label: 'Vacancy Rate',
    description: 'Expected annual vacancy percentage',
    unit: 'percent',
    range: { min: 0, max: 20, step: 0.5 },
    presets: makePresets({
      'excellent': { value: 2.0, label: 'Excellent (2%)', description: 'Premium location, high demand', citation: 'Newport Beach Rental Market Study', link: 'https://ipropertymanagement.com/research/rental-vacancy-rate' },
      'good': { value: 5.0, label: 'Good (5%)', description: 'Standard coastal market assumption', citation: 'Apartment List Vacancy Rates', link: 'https://www.northmarq.com/insights/insights/rents-inch-higher-despite-multifamily-vacancy-uptick-orange-county' },
      'average': { value: 8.0, label: 'Average (8%)', description: 'National rental market average', citation: 'Census Bureau Housing Vacancy Survey', link: 'https://www.census.gov/housing/hvs/' },
      'challenging': { value: 12.0, label: 'Challenging (12%)', description: 'Difficult market or property issues', citation: 'Real Estate Investment Analysis', link: 'https://www.census.gov/housing/hvs/' },
    }),
  }),

  // Management Fee
  managementFee: assumption('managementFee', 8.0, {
    label: 'Management Fee',
    description: 'Property management fee as percentage of rent',
    unit: 'percent',
    range: { min: 0, max: 15, step: 0.5 },
    presets: makePresets({
      'self_managed': { value: 0.0, label: 'Self-Managed (0%)', description: 'Owner handles all management', citation: 'BiggerPockets Self-Management Guide', link: 'https://www.biggerpockets.com/blog/property-management-vs-self-management' },
      'low': { value: 4.0, label: 'Low (4%)', description: 'Basic property management services', citation: 'Property Management Fee Survey', link: 'https://ipropertymanagement.com/research/average-property-management-fees' },
      'standard': { value: 8.0, label: 'Standard (8%)', description: 'Full-service property management', citation: 'NARPM Management Fee Study', link: 'https://ipropertymanagement.com/research/average-property-management-fees' },
      'premium': { value: 12.0, label: 'Premium (12%)', description: 'Luxury property full-service management', citation: 'Orange County PM Companies', link: 'https://ipropertymanagement.com/research/average-property-management-fees' },
    }),
  }),

  // Maintenance Rate
  maintenanceRate: assumption('maintenanceRate', 0.5, {
    label: 'Maintenance Rate',
    description: 'Annual maintenance as percentage of property value',
    unit: 'percent',
    range: { min: 0.1, max: 2, step: 0.1 },
    presets: makePresets({
      'new_property': { value: 0.25, label: 'New Property (0.25%)', description: 'Recently built or renovated', citation: 'Real Estate Investment Analysis', link: 'https://www.rentwell.com/blog/how-much-should-i-budget-for-rental-property-maintenance' },
      'well_maintained': { value: 0.5, label: 'Well Maintained (0.5%)', description: 'Good condition, regular upkeep', citation: 'Property Management Maintenance Costs', link: 'https://www.rentwell.com/blog/how-much-should-i-budget-for-rental-property-maintenance' },
      'older_coastal': { value: 1.0, label: 'Older Coastal (1.0%)', description: '1954 coastal home - salt air corrosion, aging systems', citation: 'Coastal Property Maintenance Studies', link: 'https://www.rentwell.com/blog/how-much-should-i-budget-for-rental-property-maintenance' },
      'older_property': { value: 1.5, label: 'Older Property (1.5%)', description: 'Higher maintenance needs, major systems aging', citation: 'Home Maintenance Cost Studies', link: 'https://www.rentwell.com/blog/how-much-should-i-budget-for-rental-property-maintenance' },
    }),
  }),

  // Insurance Cost
  insuranceCost: assumption('insuranceCost', 7000, {
    label: 'Annual Insurance Cost',
    description: 'Annual homeowners/landlord insurance premium',
    unit: 'currency',
    range: { min: 2000, max: 20000, step: 500 },
    presets: makePresets({
      'low': { value: 4000, label: 'Low ($4,000)', description: 'Basic coverage, if available', citation: 'CA Insurance Market 2025', link: 'https://www.insurance.ca.gov/' },
      'coastal_ca': { value: 7000, label: 'Coastal CA ($7,000)', description: 'Realistic for $2M+ coastal property in current CA market crisis', citation: 'CA Insurance Commissioner Reports', link: 'https://www.insurance.ca.gov/' },
      'high': { value: 10000, label: 'High ($10,000)', description: 'Premium coverage or difficulty obtaining standard policy', citation: 'CA FAIR Plan + Supplemental', link: 'https://www.cfpnet.com/' },
      'very_high': { value: 15000, label: 'Very High ($15,000)', description: 'CA FAIR Plan with full supplemental in high-risk area', citation: 'CA FAIR Plan Rates', link: 'https://www.cfpnet.com/' },
    }),
  }),

  // Selling Costs
  sellingCosts: assumption('sellingCosts', 6.0, {
    label: 'Selling Costs',
    description: 'Total costs to sell property (commissions, fees, closing)',
    unit: 'percent',
    range: { min: 2, max: 12, step: 0.5 },
    presets: makePresets({
      'low': { value: 4.5, label: 'Low (4.5%)', description: 'FSBO or discount broker', citation: 'Clever Real Estate Study', link: 'https://listwithclever.com/research/home-selling-costs/' },
      'typical': { value: 6.0, label: 'Typical (6%)', description: 'Standard realtor commission plus fees', citation: 'National Association of Realtors', link: 'https://www.nar.realtor/research-and-statistics/quick-real-estate-statistics' },
      'high': { value: 7.5, label: 'High (7.5%)', description: 'Premium service or challenging market', citation: 'Zillow Selling Costs Report', link: 'https://www.zillow.com/sellers-guide/costs-of-selling/' },
      'very_high': { value: 9.0, label: 'Very High (9%)', description: 'Luxury market or extensive preparation', citation: 'Luxury Real Estate Sales Data', link: 'https://www.realtor.com/advice/sell/how-much-does-it-cost-to-sell-a-house/' },
    }),
  }),

  // Rental Setup Costs
  rentalSetupCosts: assumption('rentalSetupCosts', 15000, {
    label: 'Rental Setup Costs',
    description: 'One-time costs to prepare property for rental',
    unit: 'currency',
    range: { min: 0, max: 50000, step: 1000 },
    presets: makePresets({
      'minimal': { value: 5000, label: 'Minimal ($5K)', description: 'Basic cleaning and minor repairs', citation: 'BiggerPockets Rental Guide', link: 'https://www.biggerpockets.com/blog/estimating-rental-property-expenses' },
      'standard': { value: 15000, label: 'Standard ($15K)', description: 'Professional cleaning, painting, minor updates', citation: 'BiggerPockets Property Prep', link: 'https://www.biggerpockets.com/blog/estimating-rental-property-expenses' },
      'moderate': { value: 25000, label: 'Moderate ($25K)', description: 'Flooring, appliances, significant updates', citation: 'HomeLight Renovation Guide', link: 'https://www.homelight.com/blog/seller-closing-costs/' },
      'extensive': { value: 40000, label: 'Extensive ($40K)', description: 'Major renovations, luxury finishes', citation: 'National REIA', link: 'https://nationalreia.org/' },
    }),
  }),

  // Capital Gains Tax Rate
  capitalGainsRate: assumption('capitalGainsRate', 25.0, {
    label: 'Capital Gains Tax Rate',
    description: 'Combined federal + state capital gains rate',
    unit: 'percent',
    range: { min: 15, max: 35, step: 0.5 },
    presets: {
      'federal_only': {
        value: 20,
        label: 'Federal Only (20%)',
        description: 'Federal long-term capital gains (high bracket)',
        citation: { source: 'IRS', url: 'https://www.irs.gov/taxtopics/tc409' },
      },
      'with_niit': {
        value: 23.8,
        label: 'Federal + NIIT (23.8%)',
        description: 'Federal 20% + Net Investment Income Tax 3.8%',
        citation: { source: 'IRS', url: 'https://www.irs.gov/taxtopics/tc559' },
      },
      'with_ca': {
        value: 25,
        label: 'Federal + NIIT + CA (25%)',
        description: 'Approximate combined rate for high earners in CA',
        citation: { source: 'Tax Foundation', url: 'https://taxfoundation.org/data/all/state/state-income-tax-rates/' },
      },
    },
  }),

  // Rental Income Tax Rate
  rentalIncomeTaxRate: assumption('rentalIncomeTaxRate', 30.0, {
    label: 'Rental Income Tax Rate',
    description: 'Marginal tax rate on rental income',
    unit: 'percent',
    range: { min: 20, max: 50, step: 1 },
    presets: {
      'moderate': {
        value: 25,
        label: 'Moderate (25%)',
        description: 'Lower income bracket or deductions',
        citation: { source: 'IRS', url: 'https://www.irs.gov/taxtopics/tc414' },
      },
      'typical': {
        value: 30,
        label: 'Typical (30%)',
        description: 'Mid-to-upper income bracket',
        citation: { source: 'IRS', url: 'https://www.irs.gov/taxtopics/tc414' },
      },
      'high': {
        value: 37,
        label: 'High (37%)',
        description: 'Top federal + CA marginal rate',
        citation: { source: 'Tax Foundation', url: 'https://taxfoundation.org/data/all/state/state-income-tax-rates/' },
      },
    },
  }),

  // Depreciation enabled
  depreciation: assumption('depreciation', 1, {
    label: 'Take Depreciation',
    description: 'Whether to claim depreciation deduction (1=yes, 0=no)',
    unit: 'number',
    range: { min: 0, max: 1, step: 1 },
    presets: {
      'yes': { value: 1, label: 'Yes', description: 'Claim depreciation deduction', citation: { source: 'IRS Publication 527' } },
      'no': { value: 0, label: 'No', description: 'Skip depreciation (simplifies exit)', citation: { source: 'IRS Publication 527' } },
    },
  }),
};

export default assumptions;
